import { getStateFromPath, LinkingOptions } from '@react-navigation/native'
import { GlobalNavigationParamList } from '../navigationTypes'

// Addresses that used to be declared without a screen behind them: they keep
// opening the nearest real page instead of « Page introuvable ».
const LEGACY_PATHS: Record<string, string> = {
  '/note/add': '/note/edit',
  '/calendar/edit': '/calendar',
  '/keep-contact/edit': '/keep-contacts',
  '/scheduled-message/edit': '/scheduled-messages',
  '/my-profile/living-places': '/my-profile',
  '/contact': '/contacts',
  // The menu had no address: the browser showed its internal name.
  '/MenuMain': '/menu',
}

const withoutLegacyPath = (path: string): string => {
  const queryStart = path.indexOf('?')
  const pathname = queryStart === -1 ? path : path.slice(0, queryStart)
  const replacement = LEGACY_PATHS[pathname.replace(/\/+$/, '')]
  return replacement ? replacement + (queryStart === -1 ? '' : path.slice(queryStart)) : path
}

// The browser extension loads index.html#/extension/… in a frame: its address
// is not one of the app's pages and must not show « Page introuvable ».
const isExtensionAddress = (path: string): boolean =>
  path.startsWith('/index.html') ||
  (typeof window !== 'undefined' &&
    (!!window.location?.hash?.startsWith('#/extension/') || window.self !== window.top))

// Screens that cannot show anything without this parameter: an address cut
// before its « ?…= » part opens their list instead of a blank page.
const REQUIRED_PARAMS: Record<string, { param: string; listPath: string }> = {
  HashtagDetails: { param: 'hashtagId', listPath: '/hashtags' },
  FtSocialImportNetwork: { param: 'network', listPath: '/contacts/social-import' },
  FtSocialImportResults: { param: 'importId', listPath: '/contacts/social-import' },
  ComposeMessage: { param: 'scheduledMessageId', listPath: '/scheduled-messages' },
  CategoryDetails: { param: 'categoryId', listPath: '/categories' },
  EditCategory: { param: 'categoryId', listPath: '/categories' },
}

const listPathIfParamMissing = (state: ReturnType<typeof getStateFromPath>): string | undefined => {
  let route: { name: string; params?: object; state?: unknown } | undefined
  let current = state as { routes: (typeof route)[] } | undefined
  while (current?.routes?.length) {
    route = current.routes[current.routes.length - 1]
    current = route?.state as typeof current
  }
  const required = route ? REQUIRED_PARAMS[route.name] : undefined
  if (!required) return undefined
  return (route?.params as Record<string, unknown> | undefined)?.[required.param]
    ? undefined
    : required.listPath
}

export const linkingConfig: LinkingOptions<GlobalNavigationParamList> = {
  prefixes: [
    'http://localhost:5173',
    'https://keeptouch.co',
    'keeptouch://',
    'chrome-extension://',
    'moz-extension://',
  ],
  config: {
    screens: {
      // Root level screens
      Auth: {
        screens: {
          Welcome: 'auth/welcome',
          Register: 'auth/register',
          Login: 'auth/login',
          LoginWithCode: 'auth/login-code',
          ForgotPassword: 'auth/forgot-password',
          ResetPassword: 'auth/reset-password',
          RegistrationSteps: 'auth/onboarding',
          FinalStep: 'auth/congrats',
        },
      },
      Tab: {
        screens: {
          ListContacts: 'contacts',
          ListMessages: 'messages',
          Notifications: 'notifications',
          Chatbot: 'chatbot',
        },
      },
      Contacts: {
        screens: {
          AddContact: 'contacts/add',
          CardScanner: 'card-scanner',
          ImportContacts: 'contacts/import',
          ListingContactsFilters: 'contacts/filters',
          FtSocialImport: 'contacts/social-import',
          FtSocialImportNetwork: 'contacts/social-import/network',
          FtSocialImportResults: 'contacts/social-import/results',
          //Conversation: 'contact-conversation',
        },
      },
      Messages: {
        screens: {
          AddConversation: 'add-conversation',
          Conversation: 'conversation/:conversationId',
          GroupConversation: 'group-conversation/:conversationId',
        },
      },
      Profile: {
        screens: {
          ProfileHome: 'my-profile',
          ProfileEdit: 'my-profile/edit',
          ProfileMemories: 'my-profile/memories',
          ProfileAddMemory: 'memories/add',
          ProfileShowMemory: 'memories/:memoryId',
          ProfileAlbumMemories: 'albums/:albumId',
          DetailsGhostProfile: 'profile/:profileId',
          AddGhostContact: 'profile/new',
          EditGhostContact: 'profile/:profileId/edit',
          ProfileContextes: 'profile/:profileId/contextes',
        },
      },
      PublicEvent: 'e/:code',
      MyTicket: 'events/:eventId/ticket',
      PublicPrivacyPolicy: 'privacy',
      SimulatedPayment: 'pay/simulated/:paymentId',
      LinkedInCallback: 'linkedin/callback',
      DemoSwitch: 'demo',
      Menu: {
        screens: {
          // Where the back arrow of the administration leads.
          MenuMain: 'menu',
          Events: {
            screens: {
              EventsList: 'events',
              EventCreate: 'events/new',
              EventManage: 'events/:eventId',
              EventEdit: 'events/:eventId/edit',
              EventCheckIn: 'events/:eventId/checkin',
              EventCheckInScan: 'events/:eventId/checkin/scan',
              OrganizerRequest: 'events/organizer-request',
              OrganizerStats: 'events/stats',
            },
          },
          Admin: {
            screens: {
              AdminHome: 'admin',
              AdminOrganizers: 'admin/organizers',
              AdminAddOrganizer: 'admin/add-organizer',
              AdminOrganizerDetail: 'admin/organizers/:accountId',
              AdminRequests: 'admin/requests',
              AdminEvents: 'admin/events',
              AdminEventDetail: 'admin/events/:eventId',
              AdminSettings: 'admin/settings',
            },
          },
          ImportContacts: 'import-contacts',
          ClassifyContacts: 'classify-contacts',
          MergeContacts: 'merge-contacts',
          Map: 'map',
          Graph: 'graph',
          Nearby: 'nearby',
          Notes: {
            screens: {
              NotesList: 'notes',
              EditNote: 'note/edit',
            },
          },
          Categories: {
            screens: {
              CategoriesList: 'categories',
              CategoryDetails: 'category',
              AddCategory: 'category/add',
              EditCategory: 'category/edit',
            },
          },
          Hashtags: {
            // The list underneath, so « Retour » leads somewhere after a reload.
            // (`as never`: the nested stacks are not described in the types.)
            initialRouteName: 'HashtagsList' as never,
            screens: {
              HashtagsList: 'hashtags',
              HashtagDetails: 'hashtag',
              AddHashtag: 'hashtag/add',
              EditHashtag: 'hashtag/edit',
            },
          },
          RelationalColors: {
            screens: {
              RelationalColorsList: 'relational-colors',
              EditRelationalColor: 'relational-color/edit',
            },
          },
          Calendar: {
            screens: {
              CalendarMain: 'calendar',
              AddEvent: 'calendar/add',
            },
          },
          Birthdays: 'birthdays',
          KeepContact: {
            initialRouteName: 'KeepContactMain' as never,
            screens: {
              KeepContactMain: 'keep-contacts',
              AddKeep: 'keep-contact/add',
            },
          },
          ScheduledMessages: {
            initialRouteName: 'ScheduledMessagesList' as never,
            screens: {
              ScheduledMessagesList: 'scheduled-messages',
              CreateScheduledMessage: 'scheduled-message/add',
              ComposeMessage: {
                path: 'scheduled-message/compose',
                // Read back from the address it is the text « false », which
                // the screen would take for true.
                parse: { isEdit: (value: string) => value === 'true' },
              },
            },
          },
          QRCode: 'qr-code',
          Scan: 'qr-code/scan',
          Settings: 'settings',
          NotificationSettings: 'settings/notifications',
          Subscription: 'subscription',
          LegalNotice: 'legal-notice',
          ContactUs: 'contact-us',
          PrivacyPolicy: 'privacy-policy',
          JoinUs: 'join-us',
        },
      },
      Extension: 'extension/:profileId/:payload',
      // Any address that is none of the above.
      NotFound: '*',
    },
  },
  getStateFromPath: (path, options) => {
    const state = getStateFromPath(withoutLegacyPath(path), options)
    if (state?.routes[0]?.name === 'NotFound' && isExtensionAddress(path)) return undefined
    const listPath = listPathIfParamMissing(state)
    return listPath ? getStateFromPath(listPath, options) : state
  },
}
